import React, { useEffect, useMemo, useState } from "react";
import { Button, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";
import { helpPageLabel, helpPages, youtubeEmbedUrl, youtubeId } from "../../helpers/helpPages";

const useStyles = makeStyles()((theme) => {
  const border = `1px solid ${theme.palette.divider}`;
  const primary = theme.palette.primary.main;

  return {
    mainPaper: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      borderRadius: 16,
      overflow: "hidden",
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 2, 1.25, 3),
      borderBottom: border,
    },
    headerText: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      flex: 1,
      minWidth: 0,
    },
    headerNav: {
      display: "flex",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    overline: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: primary,
      flexShrink: 0,
      lineHeight: 1,
    },
    title: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    counter: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    body: {
      flex: 1,
      minHeight: 0,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 320px",
      gap: theme.spacing(2.5),
      padding: theme.spacing(3),
      overflowY: "auto",
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "minmax(0, 1fr)",
      },
    },
    playerFrame: {
      width: "100%",
      maxWidth: "calc((100vh - 230px) * 16 / 9)",
    },
    player: {
      position: "relative",
      width: "100%",
      paddingTop: "56.25%",
      borderRadius: 12,
      overflow: "hidden",
      background: "#000",
      boxShadow: theme.shadows[3],
      "& iframe": {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
      },
    },
    noVideo: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "rgba(255,255,255,0.7)",
      fontSize: 14,
    },
    description: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      borderRadius: 10,
      border,
      background: alpha(theme.palette.text.primary, 0.03),
      color: theme.palette.text.secondary,
      fontSize: 14,
      lineHeight: 1.6,
      whiteSpace: "pre-line",
    },
    playlist: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      alignSelf: "start",
      maxHeight: "calc(100vh - 190px)",
      overflowY: "auto",
      paddingRight: 2,
      ...theme.scrollbarStyles,
    },
    groupTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      margin: theme.spacing(1, 0, 0.25),
      "&:first-of-type": {
        marginTop: 0,
      },
    },
    item: {
      display: "grid",
      gridTemplateColumns: "112px minmax(0, 1fr)",
      gap: theme.spacing(1.25),
      alignItems: "center",
      padding: theme.spacing(0.75),
      borderRadius: 10,
      border: "1px solid transparent",
      background: "transparent",
      color: theme.palette.text.primary,
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      transition: "background-color .15s, border-color .15s",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    itemActive: {
      borderColor: alpha(primary, 0.5),
      backgroundColor: alpha(primary, 0.08),
      "&:hover": {
        backgroundColor: alpha(primary, 0.12),
      },
    },
    thumb: {
      position: "relative",
      display: "block",
      width: 112,
      aspectRatio: "16 / 9",
      borderRadius: 8,
      overflow: "hidden",
      background: alpha(theme.palette.text.primary, 0.08),
      "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      },
    },
    thumbNumber: {
      position: "absolute",
      left: 4,
      top: 4,
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      borderRadius: 9,
      background: "rgba(0,0,0,0.7)",
      color: "#fff",
      fontSize: 10.5,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    itemText: {
      display: "block",
      minWidth: 0,
    },
    itemTitle: {
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.35,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    itemState: {
      display: "block",
      fontSize: 11.5,
      marginTop: 2,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    itemStateActive: {
      color: primary,
      fontWeight: 600,
    },
    navButton: {
      textTransform: "none",
      fontWeight: 600,
    },
    empty: {
      margin: "auto",
      padding: theme.spacing(6, 2),
      textAlign: "center",
      color: theme.palette.text.secondary,
      fontSize: 14,
    },
  };
});

const Thumbnail = ({ video, number, className, numberClass }) => {
  const id = youtubeId(video);
  return (
    <span className={className}>
      {id && <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt="" loading="lazy" />}
      <span className={numberClass}>{number}</span>
    </span>
  );
};

const groupHelps = (records) => {
  const order = helpPages().map((page) => page.path);
  const groups = new Map();
  records.forEach((record) => {
    const key = record.page && order.includes(record.page) ? record.page : "";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(record);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : order.indexOf(a) - order.indexOf(b)))
    .map(([page, items]) => ({
      page,
      label: page ? helpPageLabel(page) : i18n.t("helps.general"),
      items,
    }));
};

const Helps = () => {
  const { classes, cx } = useStyles();
  const { list } = useHelps();
  const [records, setRecords] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await list();
        setRecords(Array.isArray(data) ? data : []);
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = useMemo(() => groupHelps(records), [records]);
  const ordered = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  const index = Math.max(0, ordered.findIndex((record) => record.id === selectedId));
  const current = ordered[index];
  const embedUrl = current ? youtubeEmbedUrl(current.video) : null;

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>{i18n.t("helps.title")}</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {!current ? (
          <Typography className={classes.empty}>{loaded ? i18n.t("helps.empty") : ""}</Typography>
        ) : (
          <>
            <div className={classes.header}>
              <div className={classes.headerText}>
                <div className={classes.overline}>
                  <OndemandVideoOutlinedIcon sx={{ fontSize: 15 }} />
                  {i18n.t("helps.overline", {
                    section: current.page ? helpPageLabel(current.page) : i18n.t("helps.general"),
                  })}
                </div>
                <Typography className={classes.title} title={current.title}>
                  {current.title}
                </Typography>
                <div className={classes.counter}>
                  {i18n.t("helps.counter", { current: index + 1, total: ordered.length })}
                </div>
              </div>
              {ordered.length > 1 && (
                <div className={classes.headerNav}>
                  <Button
                    size="small"
                    className={classes.navButton}
                    startIcon={<ChevronLeftIcon />}
                    disabled={index === 0}
                    onClick={() => setSelectedId(ordered[index - 1].id)}
                  >
                    {i18n.t("helps.previous")}
                  </Button>
                  <Button
                    size="small"
                    className={classes.navButton}
                    variant="contained"
                    disableElevation
                    endIcon={<ChevronRightIcon />}
                    disabled={index === ordered.length - 1}
                    onClick={() => setSelectedId(ordered[index + 1].id)}
                  >
                    {i18n.t("helps.next")}
                  </Button>
                </div>
              )}
            </div>

            <div className={classes.body}>
              <div className={classes.playerFrame}>
                <div className={classes.player}>
                  {embedUrl ? (
                    <iframe
                      key={current.id}
                      src={embedUrl}
                      title={current.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className={classes.noVideo}>{i18n.t("helps.noVideo")}</div>
                  )}
                </div>
                {current.description && <div className={classes.description}>{current.description}</div>}
              </div>

              <div className={classes.playlist}>
                {groups.map((group) => (
                  <React.Fragment key={group.page || "general"}>
                    <div className={classes.groupTitle}>{group.label}</div>
                    {group.items.map((record) => {
                      const position = ordered.indexOf(record);
                      const active = position === index;
                      return (
                        <button
                          key={record.id}
                          type="button"
                          className={cx(classes.item, { [classes.itemActive]: active })}
                          onClick={() => setSelectedId(record.id)}
                        >
                          <Thumbnail
                            video={record.video}
                            number={position + 1}
                            className={classes.thumb}
                            numberClass={classes.thumbNumber}
                          />
                          <span className={classes.itemText}>
                            <span className={classes.itemTitle}>{record.title}</span>
                            <span className={cx(classes.itemState, { [classes.itemStateActive]: active })}>
                              {active ? i18n.t("helps.playing") : record.description || " "}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>

          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Helps;
